import type {EditorState, Extension} from '@codemirror/state'
import {EditorView} from '@codemirror/view'
import type {Link, Nodes} from 'mdast'

import {serializeMarkdown} from '../../language-tools/markdown/serialize-markdown'
import {getNodeAtOffset} from '../../language-tools/syntax-tree/node-range-utils'
import type {ParentsOf} from '../../language-tools/syntax-tree/type-utils'
import {isUrl, setOfAll} from '../../utils'
import {hasNonEmptySelection} from '../utils'
import {getMarkdownTree} from './markdown-parser'
import {isShiftPressed, shiftPressedExtension} from './shift-pressed'

type LinkParent = ParentsOf<Link>

// The explicit signature broadens the type so that TS will allow us to call `.has(node.type)` for any node
const linkParentNodeTypes: Set<Nodes['type']> = setOfAll<LinkParent['type']>()(
  'delete',
  'emphasis',
  'heading',
  'link',
  'linkReference',
  'paragraph',
  'strong',
  'tableCell',
  'root',
)

/** Check if this node is allowed to contain a `Link` node. */
function isLinkParent(node: Nodes): node is LinkParent {
  return linkParentNodeTypes.has(node.type)
}

/**
 * If this is a URL paste and the selected text is a valid place to create a link, transforms the pasted text into a
 * Markdown hyperlink.
 */
function transformPastedUrlToLink(pastedText: string, state: EditorState) {
  if (isShiftPressed(state)) return pastedText
  if (!hasNonEmptySelection(state)) return pastedText
  if (!pastedText || !isUrl(pastedText)) return pastedText

  const selection = state.selection
  const {from, to} = selection.main
  const tree = getMarkdownTree(state)
  const fromNode = getNodeAtOffset(tree, from)
  const toNode = getNodeAtOffset(tree, to)

  // Creating a link that would cross a node boundary would create invalid Markdown
  if (fromNode !== toNode) return pastedText

  // Links are only allowed inside of certain node types. This ensures we don't create a link in a code block, for example
  if (!fromNode || !isLinkParent(fromNode)) return pastedText

  // Using `serializeMarkdown` on a link node is smarter than just doing `"[text](url)"` ourselves. For example, it will
  // automatically escape brackets in the link text and URL
  return serializeMarkdown({
    type: 'link',
    children: [{type: 'text', value: state.sliceDoc(from, to)}],
    url: pastedText,
  })
}

const urlClipboardFilter = EditorView.clipboardInputFilter.of(transformPastedUrlToLink)

/**
 * Creates Markdown links when URLs are pasted onto selected text.
 */
export const pasteLinksExtension: Extension = [shiftPressedExtension, urlClipboardFilter]
